import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';

export default function AboutPage() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const faqs = [
    {
      q: '91影视APP的团队规模多大？',
      a: '91影视拥有一支超过200人的专业团队，涵盖产品研发、内容运营、技术支持和客户服务等多个部门。团队成员来自国内外知名互联网公司，拥有丰富的行业经验和技术实力。',
    },
    {
      q: '91影视APP如何保证内容质量？',
      a: '我们建立了严格的内容审核机制，所有影视资源均经过多重质量检测，确保画质清晰、字幕准确、播放流畅。同时，我们与多家正版内容方保持合作关系，持续引入优质影视内容。',
    },
    {
      q: '如何成为91影视的合作伙伴？',
      a: '我们欢迎内容方、广告主和渠道伙伴与我们合作。请发送邮件至 business@91jingpinys.com，我们的商务团队将在3个工作日内与您取得联系。',
    },
    {
      q: '91影视APP支持哪些支付方式？',
      a: '目前支持微信支付和支付宝两种主流支付方式。我们采用银行级别的加密技术保护您的支付信息，确保每一笔交易安全可靠。',
    },
  ];

  const milestones = [
    { year: '2019', title: '项目启动', desc: '核心团队组建，91影视APP正式立项开发' },
    { year: '2020', title: '1.0版本发布', desc: '首版APP上线，提供基础影视播放功能' },
    { year: '2021', title: '用户突破1000万', desc: '凭借优质体验，用户量快速增长，突破千万大关' },
    { year: '2022', title: '4K画质升级', desc: '全面升级播放引擎，支持4K超清和HDR播放' },
    { year: '2023', title: '用户突破3000万', desc: '持续优化产品，获得用户广泛好评' },
    { year: '2024', title: 'AI智能推荐上线', desc: '引入AI技术，实现个性化内容推荐' },
    { year: '2025', title: '全面升级', desc: '全新UI设计，功能全面升级，用户突破5000万' },
  ];

  const testimonials = [
    {
      name: '张先生',
      role: '资深影迷',
      content: '用了两年多了，画质清晰、资源丰富，是我用过最好的影视APP。离线下载功能出差神器！',
      rating: 5,
    },
    {
      name: '李女士',
      role: '追剧达人',
      content: '追韩剧必备！更新快、字幕全，还有弹幕互动，看剧再也不孤单了。已经推荐给了好多朋友。',
      rating: 5,
    },
    {
      name: '王先生',
      role: '数码爱好者',
      content: '4K画质真的很惊艳，配合家里的75寸电视，效果不输影院。投屏功能也特别好用。',
      rating: 5,
    },
    {
      name: '赵女士',
      role: '学生用户',
      content: '免费资源很多，界面也简洁好用。晚上宿舍断网后就用离线下载看，一个月省了好多流量。',
      rating: 4,
    },
    {
      name: '刘先生',
      role: '上班族',
      content: '通勤路上必备APP，智能推荐总能帮我找到好看的片子。多端同步让我在家用电视看，出门用手机接着看。',
      rating: 5,
    },
    {
      name: '陈女士',
      role: '宝妈',
      content: '少儿频道的资源很棒，孩子看动画学知识，还有观看时长限制，很贴心的设计。',
      rating: 5,
    },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91精品影视',
    url: 'https://91jingpinys.com',
    description: '91精品影视 - 致力于为用户提供优质的手机观影体验，海量高清影视资源，随时随地畅享观影乐趣。',
    foundingDate: '2019',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'support@91jingpinys.com',
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于91精品影视',
    description: '91精品影视品牌介绍、发展历程、用户口碑和联系方式',
    url: 'https://91jingpinys.com/about',
  };

  return (
    <Layout>
      <SEO
        title="关于我们"
        description="91精品影视品牌介绍 - 成立于2019年，致力于为用户提供优质的手机观影体验。5000万+用户的共同选择，了解更多关于我们的故事、发展历程和用户口碑。"
        keywords="关于91影视,91精品影视品牌,91影视公司介绍,91影视联系方式,91影视口碑,91影视评价"
        canonicalPath="/about"
        ogType="website"
        schemaObjects={[orgSchema, aboutSchema, faqSchema]}
      />

      <div className="pt-20 pb-4 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
          <div className="px-4 md:px-8 pb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              关于91精品影视
            </h1>
            <p className="text-sm text-foreground-600">了解我们的故事、愿景和团队</p>
          </div>
        </div>
      </div>

      {/* Brand Introduction */}
      <section className="w-full py-14 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                我们的故事
              </h2>
              <div className="space-y-3 text-sm text-foreground-700 leading-relaxed">
                <p>
                  91精品影视成立于2019年，是一支热爱影视、追求极致体验的团队。我们深知用户对于高品质观影体验的渴望，因此从创立之初就致力于打造一款真正好用的手机影视APP。
                </p>
                <p>
                  经过多年的技术积累和产品打磨，91影视APP已经发展成为拥有<strong>5000万+用户</strong>的领先影视平台。我们始终坚持以用户体验为中心，持续优化播放技术、丰富内容资源、提升服务质量。
                </p>
                <p>
                  我们的愿景是让每个人都能随时随地享受影院级的观影体验。无论你是电影爱好者、追剧达人还是动漫迷，91影视APP都为你准备了海量优质内容。
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-background-200 flex items-center justify-center">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20creative%20tech%20office%20workspace%20with%20warm%20ambient%20lighting%2C%20minimalist%20design%2C%20wooden%20desk%20with%20dual%20monitors%20showing%20video%20editing%20software%2C%20indoor%20plants%2C%20collaborative%20environment%2C%20cozy%20atmosphere%2C%20professional%20yet%20relaxed%20vibe&width=800&height=600&seq=about-team-91-03&orientation=landscape"
                alt="91精品影视团队办公环境"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="w-full py-14 bg-background-100">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
              发展历程
            </h2>
            <p className="text-sm text-foreground-600">一路走来，感谢有你</p>
          </div>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
            <div className="flex flex-col gap-6">
              {milestones.map((mile, i) => (
                <div
                  key={i}
                  className={`relative flex items-start gap-6 ${
                    i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="hidden md:block flex-1"></div>
                  <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 border-4 border-background-100 -translate-x-1/2 mt-1.5 z-10"></div>
                  <div className="flex-1 pl-12 md:pl-0">
                    <div className={`p-4 rounded-xl bg-background-50 border border-background-200/70 ${i % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}`}>
                      <span className="inline-block px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold mb-2">
                        {mile.year}
                      </span>
                      <h3 className="text-sm font-bold text-foreground-900 mb-1">{mile.title}</h3>
                      <p className="text-xs text-foreground-600">{mile.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="w-full py-14 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-600">来自真实用户的评价</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <div key={i} className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  "{t.content}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-xs font-bold">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                    <div className="text-xs text-foreground-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="w-full py-14 bg-background-100">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600">有任何问题或建议？欢迎随时联系</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: 'ri-mail-line', title: '客服邮箱', value: 'support@91jingpinys.com', desc: '技术支持与使用咨询' },
              { icon: 'ri-briefcase-line', title: '商务合作', value: 'business@91jingpinys.com', desc: '广告投放与渠道合作' },
              { icon: 'ri-customer-service-2-line', title: '在线客服', value: '7×24小时在线', desc: 'APP内联系在线客服' },
              { icon: 'ri-map-pin-line', title: '公司地址', value: '中国·北京', desc: '北京市朝阳区' },
            ].map((contact, i) => (
              <div key={i} className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mx-auto mb-3">
                  <i className={`${contact.icon} text-lg`}></i>
                </div>
                <h3 className="text-sm font-bold text-foreground-900 mb-1">{contact.title}</h3>
                <p className="text-sm text-primary-600 font-medium mb-1">{contact.value}</p>
                <p className="text-xs text-foreground-500">{contact.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full py-14 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
              常见问题
            </h2>
            <p className="text-sm text-foreground-600">关于我们和产品的常见疑问</p>
          </div>
          <div className="flex flex-col gap-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="rounded-xl bg-background-100 border border-background-200/70 overflow-hidden"
              >
                <button
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                >
                  <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                  <i
                    className={`text-foreground-500 text-sm transition-transform duration-300 flex-shrink-0 ${
                      activeFaq === i ? 'ri-subtract-line' : 'ri-add-line'
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    activeFaq === i ? 'max-h-60' : 'max-h-0'
                  }`}
                >
                  <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}